/**
 * The cabinet's memory: what a run was worth, and the ten best ever played.
 *
 * Nothing in this file touches the DOM. It is the arithmetic and the storage,
 * so `tests/highscore.test.ts` can drive the whole of it in Node — which
 * matters, because the one property this has to guarantee is a property about
 * numbers, not about pixels.
 *
 * ─────────────────────────────────────────────────────────────── the score ──
 *
 * Rounds cleared is the spine. Everything else is a tiebreak, and a tiebreak
 * that can outrun the thing it breaks ties in is not a tiebreak.
 *
 *   round    2000   a round cleared
 *   kill        1   a body killed, over the whole run
 *   life        5   a life still held when the run ended
 *
 * 2000 is derived, not picked. The most the other two terms can pay for a
 * single round is `MAX_BODIES_PER_ROUND` kills — 900 — plus the 100 starting
 * lives at 5 apiece, so 1400 is the ceiling on one round's worth of everything
 * that is not a round. The round weight sits above it, which makes the claim
 * exact: *the marginal round always pays more than the marginal round's kills
 * and lives together*, whatever else is going on.
 *
 * The first draft used 1000, which cleared the kill ceiling and not the pair —
 * a full-lives run could have outranked a run one round deeper. In practice it
 * could not, because a defeat leaves zero lives and the life term is only ever
 * nonzero on a completed board, but a weighting that is safe by accident of the
 * loss condition is a weighting that breaks the day the loss condition moves.
 * The test asserts the ceiling, not the accident.
 *
 * It is deliberately not the stronger claim that a deeper run always outranks a
 * shallower one. Forty rounds of clean clearing against thirty-six is a real
 * gap and kills are how it shows; a board that flattened it would be reporting
 * one number and ranking on another.
 *
 * **Time is not in here.** The brief offered it and the round clock is the
 * wrong clock: rounds are paced by `Waves`, not by the player, and the speed
 * control turns elapsed seconds into a settings value rather than a skill. A
 * player who leaves the game on 1x would sit below an identical run on 3x
 * forever, having done nothing worse. Lives remaining answers the same question
 * — how cleanly did you hold — out of something the player actually decides.
 */

/** What one weight buys. Exported so the test asserts against these, not copies. */
export const SCORE_WEIGHTS = {
  round: 2000,
  kill: 1,
  life: 5,
} as const;

/** How many entries the board keeps. Ten is what a cabinet shows. */
export const BOARD_SIZE = 10;

/** Characters an entry may be made of. Digits included; `Ø` is not a letter. */
export const INITIALS_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/** The `localStorage` key. Versioned, so a format change cannot half-load. */
export const STORAGE_KEY = 'pixeltd.scores.v1';

/** The parts of a finished run the score is made of. */
export interface RunOutcome {
  /** Round the run ended on. */
  round: number;
  kills: number;
  /** Lives still held at the end. Zero on a defeat, by definition. */
  livesRemaining: number;
}

/** One row of the board. */
export interface ScoreEntry extends RunOutcome {
  /** Exactly three characters from {@link INITIALS_ALPHABET}. */
  initials: string;
  score: number;
  /** `Date.now()` at the moment it was set. Breaks exact ties in favour of the
      older run: a new score has to actually beat one to take its place. */
  at: number;
}

/** What a run was worth. */
export function scoreRun(run: RunOutcome): number {
  const round = Math.max(0, Math.floor(run.round));
  const kills = Math.max(0, Math.floor(run.kills));
  const lives = Math.max(0, Math.floor(run.livesRemaining));
  return round * SCORE_WEIGHTS.round
    + kills * SCORE_WEIGHTS.kill
    + lives * SCORE_WEIGHTS.life;
}

/**
 * Ordering. Higher score first; an exact tie goes to whoever set it first.
 *
 * Without the timestamp a tie is resolved by whatever order the array happened
 * to be in, which is stable but arbitrary — and it would let a player who ties
 * the top score displace it by replaying, which reads as a bug from the seat.
 */
function compare(a: ScoreEntry, b: ScoreEntry): number {
  return b.score - a.score || a.at - b.at;
}

/** Trims to three characters from the alphabet, padded with `A`. */
export function normaliseInitials(raw: string): string {
  const up = [...String(raw ?? '').toUpperCase()]
    .filter((c) => INITIALS_ALPHABET.includes(c))
    .slice(0, 3)
    .join('');
  return up.padEnd(3, 'A');
}

/**
 * Reads and writes the board.
 *
 * `localStorage` is not assumed to exist and not assumed to work. Safari in
 * private browsing throws on `setItem` from a quota that is always zero, an
 * iframe with a restrictive sandbox throws on the *getter*, and a JSDOM test
 * has no storage at all. None of those are worth ending a run over, so every
 * path degrades to an in-memory board that behaves identically and forgets on
 * reload.
 */
export class HighScores {
  private entries: ScoreEntry[] = [];
  private loaded = false;

  constructor(private readonly key: string = STORAGE_KEY) {}

  /** The board, best first. A copy — callers render it, they do not own it. */
  list(): ScoreEntry[] {
    this.ensure();
    return this.entries.slice();
  }

  /** Would this score take a place? An empty board says yes to anything > 0. */
  qualifies(score: number): boolean {
    this.ensure();
    if (score <= 0) return false;
    if (this.entries.length < BOARD_SIZE) return true;
    return score > this.entries[this.entries.length - 1].score;
  }

  /**
   * Files a run. Returns the index it landed at, or `-1` if it did not place.
   *
   * The caller decides whether to offer entry at all — {@link qualifies} — but
   * this re-checks rather than trusting it, because the two calls are separated
   * by however long the player spent picking three letters.
   */
  submit(run: RunOutcome, initials: string, now = Date.now()): number {
    this.ensure();
    const entry: ScoreEntry = {
      initials: normaliseInitials(initials),
      score: scoreRun(run),
      round: Math.max(0, Math.floor(run.round)),
      kills: Math.max(0, Math.floor(run.kills)),
      livesRemaining: Math.max(0, Math.floor(run.livesRemaining)),
      at: now,
    };
    if (!this.qualifies(entry.score)) return -1;

    this.entries.push(entry);
    this.entries.sort(compare);
    this.entries.length = Math.min(this.entries.length, BOARD_SIZE);
    this.persist();
    return this.entries.indexOf(entry);
  }

  /** Empties the board, on disk as well as in memory. */
  clear(): void {
    this.entries = [];
    this.loaded = true;
    try {
      storage()?.removeItem(this.key);
    } catch {
      /* see the class note */
    }
  }

  private ensure(): void {
    if (this.loaded) return;
    this.loaded = true;
    let raw: string | null = null;
    try {
      raw = storage()?.getItem(this.key) ?? null;
    } catch {
      return;
    }
    if (!raw) return;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      this.entries = parsed.filter(isEntry).sort(compare).slice(0, BOARD_SIZE);
    } catch {
      // A corrupt board is an empty board. Throwing here would take the whole
      // end-of-run sheet with it, which is a worse outcome than a lost table.
      this.entries = [];
    }
  }

  private persist(): void {
    try {
      storage()?.setItem(this.key, JSON.stringify(this.entries));
    } catch {
      /* see the class note */
    }
  }
}

/** A row survives the round trip only if every field is the shape it claims. */
function isEntry(v: unknown): v is ScoreEntry {
  if (typeof v !== 'object' || v === null) return false;
  const e = v as Record<string, unknown>;
  return typeof e.initials === 'string'
    && e.initials.length === 3
    && Number.isFinite(e.score)
    && Number.isFinite(e.round)
    && Number.isFinite(e.kills)
    && Number.isFinite(e.livesRemaining)
    && Number.isFinite(e.at);
}

/** `localStorage`, or `null` where there is none. The getter itself can throw. */
function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
